import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { OrganizationConfiguration } from "../../models/OrganizationConfiguration/OrganizationConfiguration";
import { CaseMigrationStrategy } from "../../models/OrganizationConfiguration/CaseMigrationStrategy";
import { organizationConfigurationSchema } from "@/lib/schemas/organizationConfigurationSchema";
import OrganizationsRepository from "../../repositories/OrganizationsRepository";
import { ImplementationType, offersCustomCaseStrategy } from "../../models/OrganizationConfiguration/ImplementationType";
import OrganizationFilesSynchronizationStrategySection from "./OrganizationFilesSynchronizationStrategySection";
import ImplementationTypeSelect from "../ClientConfiguration/ImplementationTypeSelect";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import DropboxApiAuthConfigurationSection from "./DropboxApiAuthConfigurationSection";
import MicrosoftApiAuthConfigurationSection from "./MicrosoftApiAuthConfigurationSection";
import { OrganizationWithStatistics } from "../../models/OrganizationWithStatistics";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import {UserRole} from "../../models/User";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {QUERY_KEYS} from "../../constants/QueryKeys";
import { reportAndScrollToFormErrors } from "@/lib/form-utils";
import { RECALCULATION_WARNINGS } from "../../constants/RecalculationWarnings";
import { MigrationType, MigrationTypeLabels } from "../../models/Statistics/MigrationType";
import RecalculationQueueBanner from "./RecalculationQueueBanner";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface OrganizationConfigurationFormProps {
  organizationId?: string;
  configuration: OrganizationConfiguration;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const normalizeList = (values?: string[] | null): string =>
  (values ?? []).map((v) => (v ?? "").trim()).filter((v) => v.length > 0).join(";");

const normalizeStrategySet = (values?: CaseMigrationStrategy[] | null): string =>
  [...(values ?? [])].sort().join(",");

const normalizeDate = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value.toISOString();
  const parsed = new Date(value as string);
  return isNaN(parsed.getTime()) ? String(value) : parsed.toISOString();
};

/**
 * Which migration types a pending save will recalculate, mirroring the server trigger set in
 * PatchOrganizationConfigurationDto: any case strategy / date-window / toggle / case-number list change recalculates
 * Cases + Intakes; each of the provider / firm-documents / template strategies recalculates its own type. Prioritized
 * case file ids recalculate nothing: the server reads them when it hands files out.
 * Kept intentionally over-inclusive — a false positive only shows the warning, whereas a miss would hide it.
 */
const computeAffectedRecalcTypes = (
  original: OrganizationConfiguration,
  next: OrganizationConfiguration,
): MigrationType[] => {
  const caseChanged =
    normalizeStrategySet(original.casesMigrationStrategy) !== normalizeStrategySet(next.casesMigrationStrategy) ||
    !!original.migrateOpenedCases !== !!next.migrateOpenedCases ||
    !!original.migrateClosedCases !== !!next.migrateClosedCases ||
    normalizeDate(original.casesOpenedAfter) !== normalizeDate(next.casesOpenedAfter) ||
    normalizeDate(original.casesOpenedBefore) !== normalizeDate(next.casesOpenedBefore) ||
    normalizeDate(original.casesClosedAfter) !== normalizeDate(next.casesClosedAfter) ||
    normalizeDate(original.casesClosedBefore) !== normalizeDate(next.casesClosedBefore) ||
    normalizeList(original.caseSpecifiedList) !== normalizeList(next.caseSpecifiedList) ||
    normalizeList(original.casePriorityList) !== normalizeList(next.casePriorityList) ||
    normalizeList(original.caseIgnoredList) !== normalizeList(next.caseIgnoredList);

  const types: MigrationType[] = [];
  if (caseChanged) types.push(MigrationType.CasesMigration, MigrationType.IntakesMigration);
  if (original.providersMigrationStrategy !== next.providersMigrationStrategy)
    types.push(MigrationType.ProvidersMigration);
  if (original.firmDocumentsMigrationStrategy !== next.firmDocumentsMigrationStrategy)
    types.push(MigrationType.FirmDocumentsMigration);
  if (original.templateFilesMigrationStrategy !== next.templateFilesMigrationStrategy)
    types.push(MigrationType.TemplatesMigration);
  return types;
};

const buildRecalcWarnings = (types: MigrationType[]): string[] => [
  `Saving will start a background priority recalculation for: ${types.map((t) => MigrationTypeLabels[t]).join(", ")}.`,
  "All changes are saved together and trigger a single recalculation - make every change you intend before saving.",
  ...RECALCULATION_WARNINGS,
];

const OrganizationConfigurationForm: React.FC<OrganizationConfigurationFormProps> = ({
  organizationId,
  configuration,
  userRole,
  isAssignedUser,
}) => {
  const organizationsRepository = new OrganizationsRepository();
  const queryClient = useQueryClient();
  const [organization, setOrganization] = useState<OrganizationWithStatistics | undefined>(undefined);
  const [isUploading, setUploading] = React.useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRecalcTypes, setPendingRecalcTypes] = useState<MigrationType[]>([]);
  // Editors touching an org they are not assigned to must supply an audit reason; everyone else does not.
  const requiresReason = useRequiresReason();

  // Large list fields are managed outside RHF to keep form-state mutations
  // O(1). Putting 50k+ arrays into RHF makes every unrelated setValue
  // (date pickers, switches) take ~35ms+ because RHF iterates / clones / dirty-
  // checks the full form state. Lists are merged back into the payload on
  // submit. See bigLists/setListField wiring below.
  const [bigLists, setBigLists] = useState<{
    caseSpecifiedList: string[];
    casePriorityList: string[];
    caseIgnoredList: string[];
    caseFilesPriorityList: string[];
  }>(() => ({
    caseSpecifiedList: configuration.caseSpecifiedList ?? [],
    casePriorityList: configuration.casePriorityList ?? [],
    caseIgnoredList: configuration.caseIgnoredList ?? [],
    caseFilesPriorityList:
      (configuration.caseFilesPriorityList as unknown as string[]) ?? [],
  }));
  const setListField = useCallback(
    (key: keyof typeof bigLists, values: string[]) => {
      setBigLists((prev) => ({ ...prev, [key]: values }));
    },
    [],
  );

  // Strip the list fields out of RHF defaults so RHF never owns them.
  const rhfDefaults = useMemo(() => {
    const {
      caseSpecifiedList: _c1,
      casePriorityList: _c2,
      caseIgnoredList: _c3,
      caseFilesPriorityList: _c4,
      ...rest
    } = configuration;
    return rest as OrganizationConfiguration;
  }, [configuration]);

  const form = useForm<OrganizationConfiguration>({
    defaultValues: rhfDefaults,
    // Schema uses `.passthrough()` so unlisted fields (dropbox/microsoft tokens)
    // survive submit; that adds an index signature to the inferred output that
    // doesn't structurally overlap with the explicit model. Double-cast to the
    // form's working type.
    resolver: zodResolver(organizationConfigurationSchema) as unknown as Resolver<OrganizationConfiguration>,
    // Custom scroll/report flow via reportAndScrollToFormErrors — disable RHF's
    // default focus-on-error so the two don't fight.
    shouldFocusError: false,
  });

  const configMutation = useMutation({ mutationKey: [QUERY_KEYS.ORGANIZATION_CONFIGURATION, organizationId], mutationFn:
    async (params: OrganizationConfiguration & { reason?: string }) => {
      if (organizationId) {
        const { reason, ...updatedConfig } = params;
        return await organizationsRepository.patchOrganizationConfiguration(
          organizationId,
          updatedConfig,
          reason
        );
      }
    }, onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION_CONFIGURATION] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION, organizationId] });
      },
      onSettled: () => {
        setUploading(false);
      }, });

  // The consent page gets a signed link that expires, never the organization secret.
  const openAdminConsent = async (authType: "dropbox" | "microsoft") => {
    if (!organizationId) return;
    try {
      window.location.href = await organizationsRepository.createConsentLink(organizationId, authType);
    } catch {
      toast.error("Failed to create the consent link");
    }
  };

  const copyConsentLink = async (authType: "sharepoint" | "dropbox" | "microsoft") => {
    if (!organizationId) return;
    let link: string;
    try {
      link = `${window.location.origin}${await organizationsRepository.createConsentLink(organizationId, authType)}`;
    } catch {
      toast.error("Failed to create the consent link");
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Consent link copied", { description: "Send it to the firm's admin. It works for 7 days." });
    } catch {
      // The browser can refuse the clipboard (no focus, no permission). The link exists by now and each one is
      // audited, so show it for copying by hand rather than report a failure that sends the user to make another.
      toast.info("Copy the consent link", {
        description: `${link} — it works for 7 days.`,
        duration: 60_000,
      });
    }
  };

  const organizationQuery = useQuery({ queryKey: [QUERY_KEYS.ORGANIZATION, organizationId], queryFn:
    async () => {
      return await organizationsRepository.getOrganizationWithStatistics(organizationId ?? "");
    }, gcTime: 2 * 60 * 1000,
      refetchInterval: 2 * 60 * 1000,
      refetchOnMount: true, });

  useEffect(() => {
    if (organizationQuery.data) {
      setOrganization(organizationQuery.data);
    }
  }, [organizationQuery.data, organizationId]);

  const implementationType = form.watch("implementationType");

  /**
   * Strip Custom-only fields from the submit payload when they aren't actually
   * applicable. The form keeps any values the user typed across strategy/toggle
   * changes (so they don't have to re-enter them mid-session), but the DB only
   * sees fields that match the currently-selected strategy. Rules:
   *
   *  - When `Custom` isn't selected, all four date fields and both per-category
   *    toggles are forced to null/false.
   *  - Within Custom, each pair is dropped when its category toggle is off.
   */
  const sanitizeForSubmit = (data: OrganizationConfiguration): OrganizationConfiguration => {
    const storesCustom = Array.isArray(data.casesMigrationStrategy)
      && data.casesMigrationStrategy.includes(CaseMigrationStrategy.Custom);
    const usesCustom = storesCustom && offersCustomCaseStrategy(data.implementationType);

    if (!usesCustom) {
      return {
        ...data,
        // A type that doesn't offer Custom shows it as All Cases, so that is what gets saved.
        casesMigrationStrategy: storesCustom
          ? data.casesMigrationStrategy!.map((strategy) =>
            strategy === CaseMigrationStrategy.Custom ? CaseMigrationStrategy.AllCases : strategy)
          : data.casesMigrationStrategy,
        migrateOpenedCases: false,
        migrateClosedCases: false,
        casesOpenedAfter: null,
        casesOpenedBefore: null,
        casesClosedAfter: null,
        casesClosedBefore: null,
      };
    }

    return {
      ...data,
      casesOpenedAfter: data.migrateOpenedCases ? data.casesOpenedAfter : null,
      casesOpenedBefore: data.migrateOpenedCases ? data.casesOpenedBefore : null,
      casesClosedAfter: data.migrateClosedCases ? data.casesClosedAfter : null,
      casesClosedBefore: data.migrateClosedCases ? data.casesClosedBefore : null,
    };
  };

  /**
   * Run zod validation via form.handleSubmit. On valid: mutate and toast.
   * On invalid: surface errors via the shared reporter (toast count + scroll
   * to first FieldError + fallback for hidden-field cases).
   *
   * Returns the count of validation errors (0 = submitted), so the
   * ActionConfirmationModal can decide whether to keep itself open.
   */
  const validateAndSubmit = (reason?: string): Promise<number> =>
    new Promise((resolve) => {
      form.handleSubmit(
        async (data) => {
          setUploading(true);
          try {
            // Refresh tokens are created and deleted through their own endpoints; the configuration save never
            // carries them, so a form loaded before a token was deleted cannot bring it back.
            const payload: OrganizationConfiguration = { ...sanitizeForSubmit(data), ...bigLists };
            delete payload.dropboxRefreshTokens;
            delete payload.microsoftRefreshTokens;
            await configMutation.mutateAsync({ ...payload, reason });
            toast.success("Organization configuration updated");
            resolve(0);
          } catch (error: any) {
            toast.error(error?.message ?? "Failed to update organization configuration");
            resolve(0);
          }
        },
        async () => {
          const count = await reportAndScrollToFormErrors(form);
          resolve(count);
        },
      )();
    });

  /**
   * Decide the save path. If a reason is required, or the pending change will trigger a priority
   * recalculation, open the confirmation modal (which explains the consequences and shows live queue
   * status). Otherwise submit directly, preserving the old friction-free save for non-recalc edits.
   */
  const handleSubmitClick = () => {
    const next = { ...sanitizeForSubmit(form.getValues()), ...bigLists } as OrganizationConfiguration;
    const affected = computeAffectedRecalcTypes(configuration, next);
    if (requiresReason || affected.length > 0) {
      setPendingRecalcTypes(affected);
      setConfirmOpen(true);
    } else {
      void validateAndSubmit();
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={(e) => e.preventDefault()}>
        <div
          className={cn(
            "flex mt-2 mb-4 gap-2 flex-col sm:flex-row items-start sm:items-center flex-wrap justify-between",
            isUploading && "opacity-50 pointer-events-none"
          )}
        >
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            Organization Configuration
            <DocsHelpLink to="configuration/organization-configuration" />
          </h2>
          <div>
            <Button
              type="button"
              onClick={handleSubmitClick}
              disabled={configMutation.isPending || userRole === UserRole.Viewer}
              size="sm"
            >
              Submit
            </Button>
            <ActionConfirmationModal
              // successMessage omitted: validateAndSubmit already toasts on success, so the modal must not.
              actionConfig={{ ...ACTION_CONFIGS.UPDATE_ORGANIZATION_CONFIGURATION, successMessage: undefined }}
              requireReason={requiresReason}
              warnings={pendingRecalcTypes.length > 0 ? buildRecalcWarnings(pendingRecalcTypes) : undefined}
              description={
                pendingRecalcTypes.length > 0 ? (
                  <RecalculationQueueBanner
                    organizationId={organizationId}
                    affectedTypes={pendingRecalcTypes}
                  />
                ) : undefined
              }
              open={confirmOpen}
              onClose={() => setConfirmOpen(false)}
              onConfirm={async (reason) => {
                const errorCount = await validateAndSubmit(requiresReason ? reason : undefined);
                if (errorCount > 0) {
                  // Throwing keeps the confirmation modal open. The reporter
                  // already toasted; suppress the modal's default toast.
                  const err = new Error("Validation failed") as Error & { __suppressToast?: boolean };
                  err.__suppressToast = true;
                  throw err;
                }
              }}
            />
          </div>
        </div>

        <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
          <Card className="mb-4">
            <CardContent>
              <h4 className="text-lg font-semibold">
                Implementation Type
              </h4>
              <Separator className="mt-2 mb-2" />
              <ImplementationTypeSelect
                disabled={userRole === UserRole.Viewer}
                value={implementationType || undefined}
                onChange={(value) => form.setValue("implementationType", value ?? null)}
                required
              />
            </CardContent>
          </Card>
        </div>

        <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
          <Card className="mb-4">
            <CardContent>
              <h4 className="text-lg font-semibold">
                Migration Strategy
              </h4>
              <Separator className="mt-2 mb-4" />
              <OrganizationFilesSynchronizationStrategySection
                disabled={userRole === UserRole.Viewer}
                bigLists={bigLists}
                setListField={setListField}
              />
            </CardContent>
          </Card>
        </div>

        <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
          <Card>
            <CardContent>
              <div className="flex justify-between items-center gap-4">
                <div>
                  <h4 className="text-lg font-semibold">SharePoint Admin Consent</h4>
                  <p className="text-sm text-muted-foreground">
                    A link for the firm's Microsoft 365 admin to grant consent and connect their tenant to this
                    organization. It works for 7 days.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={userRole === UserRole.Viewer}
                  onClick={() => copyConsentLink("sharepoint")}
                >
                  Copy consent link
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {implementationType === ImplementationType.Dropbox ? (
          <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
            <Card>
              <CardContent>
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-semibold">
                    Dropbox API Auth Configuration
                  </h4>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={userRole === UserRole.Viewer}
                      onClick={() => copyConsentLink("dropbox")}
                    >
                      Copy consent link
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={userRole === UserRole.Viewer}
                      onClick={() => openAdminConsent("dropbox")}
                    >
                      Authorize
                    </Button>
                  </div>
                </div>
                <Separator className="mt-2 mb-4" />
                <DropboxApiAuthConfigurationSection
                  disabled={
                    userRole === UserRole.Viewer ||
                    (!isAssignedUser && userRole !== UserRole.Admin)
                  }
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          <></>
        )}

        <div className={cn(isUploading && "opacity-50 pointer-events-none")}>
            <Card>
              <CardContent>
                <div className="flex justify-between items-center">
                  <h4 className="text-lg font-semibold">
                    Microsoft API Delegated Auth Configuration
                  </h4>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={userRole === UserRole.Viewer}
                      onClick={() => copyConsentLink("microsoft")}
                    >
                      Copy consent link
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      disabled={userRole === UserRole.Viewer}
                      onClick={() => openAdminConsent("microsoft")}
                    >
                      Authorize
                    </Button>
                  </div>
                </div>
                <Separator className="mt-2 mb-4" />
                <MicrosoftApiAuthConfigurationSection
                  disabled={
                    userRole === UserRole.Viewer ||
                    (!isAssignedUser && userRole !== UserRole.Admin)
                  }
                />
              </CardContent>
            </Card>
          </div>
      </form>
    </FormProvider>
  );
};

export default OrganizationConfigurationForm;
